import * as React from 'react';

import {
  LinkCategory,
  LinkLabelCategory,
  NodeCategory,
} from 'src/containers/ProjectDiagram/constants/categories';
import { AttributesToRemoveInfo } from 'src/containers/ProjectDiagram/hooks/remove/types';
import { useRemoveModelConceptClass } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveModelConceptClass';
import { useRemoveModelConceptRelationship } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveModelConceptRelationship';
import { useRemoveResource } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveResource';
import { useDiagramSelection } from 'src/containers/ProjectDiagram/hooks/useDiagramSelectionValue';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import {
  MappingLink,
  ModelConceptClassNode,
  ModelConceptHierarchyLink,
  ModelConceptRelationshipLink,
  ResourceNode,
} from 'src/containers/ProjectDiagram/types/diagram';
import { commitDiagramModel } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import {
  isLink,
  isLinkLabel,
  isNode,
} from 'src/containers/ProjectDiagram/utils/graphObject';
import { cleanupModelConceptHierarchyMappings } from 'src/containers/ProjectDiagram/utils/mapping/cleanupModelConceptHierarchyMappings';
import { removeMapping } from 'src/containers/ProjectDiagram/utils/mapping/removeMapping';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { removeModelConceptHierarchy } from 'src/containers/ProjectDiagram/utils/modelConceptHierarchy/removeModelConceptHierarchy';
import { projectAttributesByDomainSelector } from 'src/selectors/projectAttributesByDomain';
import { projectClassHierarchySelector } from 'src/selectors/projectClassHierarchy';
import { getAllInheritedAttributes } from 'src/utils/getAllInheritedAttributes';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

const isDeletionCandidate = (part: go.Part) =>
  isLinkLabel(part, LinkLabelCategory.MODEL_CONCEPT_HIERARCHY_LABEL) ||
  isLinkLabel(part, LinkLabelCategory.MODEL_CONCEPT_RELATIONSHIP_LABEL) ||
  isLinkLabel(part, LinkLabelCategory.MAPPING_LABEL) ||
  isLink(part, LinkCategory.MODEL_CONCEPT_HIERARCHY) ||
  isLink(part, LinkCategory.MODEL_CONCEPT_RELATIONSHIP) ||
  isLink(part, LinkCategory.MAPPING) ||
  isNode(part, NodeCategory.MODEL_CONCEPT_CLASS) ||
  isNode(part, NodeCategory.RESOURCE);

// NOTE: Order of operations matters here; otherwise, nodes could be removed
// before the relationships that point to/from them, thereby causing those
// relationships' labels and to/from values to change, thereby preventing
// things from being properly and completely deleted (e.g., labels will
// sometimes hang around detached from everything).
const getSortOrderScore = (part: go.Part) => {
  if (isLinkLabel(part)) {
    return 0;
  }
  if (isLink(part)) {
    return 1;
  }
  if (isNode(part)) {
    return 2;
  }
  return 3;
};

const compareByScore = (partOne: go.Part, partTwo: go.Part) =>
  getSortOrderScore(partOne) - getSortOrderScore(partTwo);

export const useRemoveSelection = () => {
  const { projectId } = useRouterParams();
  const diagram = useDiagram();
  const selection = useDiagramSelection();
  const hierarchyByIri = useReduxSelector(
    projectClassHierarchySelector(projectId)
  );
  const attributesByDomain = useReduxSelector(
    projectAttributesByDomainSelector(projectId)
  );
  const handleRemoveClass = useRemoveModelConceptClass();
  const handleRemoveRelationship = useRemoveModelConceptRelationship();
  const handleRemoveResource = useRemoveResource();

  return React.useCallback(() => {
    if (!diagram || !selection.size) {
      return;
    }

    const sortedSelectionToDelete = selection
      .toArray()
      .filter(isDeletionCandidate)
      .sort(compareByScore);

    let changedHierarchy = false;

    const attributesToRemoveInfo: AttributesToRemoveInfo[] = [];

    commitDiagramModel(
      diagram,
      (model, diagram) => {
        sortedSelectionToDelete.forEach((part) => {
          if (
            isNode<ModelConceptClassNode>(
              part,
              NodeCategory.MODEL_CONCEPT_CLASS
            )
          ) {
            const currentAttributesToRemoveInfo: AttributesToRemoveInfo = {
              attributesToRemove: [],
              classIri: '',
            };
            const { key } = part.data;
            const currentClassIri = getModelConceptClassValue(key);
            currentAttributesToRemoveInfo.classIri = currentClassIri;

            const currentAttributes = getAllInheritedAttributes({
              classIri: currentClassIri,
              hierarchyByIri,
              attributesByDomain,
            });

            currentAttributesToRemoveInfo.attributesToRemove = Object.values(
              currentAttributes
            ).filter((attribute) => {
              const domains = Object.values(attribute.domains);

              // Check if attribute is associated only to this class
              return (
                domains.length === 1 && domains[0].value === currentClassIri
              );
            });

            attributesToRemoveInfo.push(currentAttributesToRemoveInfo);

            handleRemoveClass({
              diagram,
              key: part.data.key,
            });
          } else if (isNode<ResourceNode>(part, NodeCategory.RESOURCE)) {
            handleRemoveResource({
              diagram,
              key: part.data.key,
            });
          } else if (
            isLink<ModelConceptRelationshipLink>(
              part,
              LinkCategory.MODEL_CONCEPT_RELATIONSHIP
            )
          ) {
            handleRemoveRelationship({
              diagram,
              key: part.data.key,
            });
          } else if (
            isLink<ModelConceptHierarchyLink>(
              part,
              LinkCategory.MODEL_CONCEPT_HIERARCHY
            )
          ) {
            removeModelConceptHierarchy(part.data)(model);
            changedHierarchy = true;
          } else if (isLink<MappingLink>(part, LinkCategory.MAPPING)) {
            removeMapping(part.data)(model);
          }
          // Note that labels are not explicitly removed here, as they get
          // removed when their links are removed. "Hanging" labels are not
          // allowed.
        });

        if (changedHierarchy) {
          cleanupModelConceptHierarchyMappings(model);
        }
      },
      { attributesToRemoveInfo }
    );
  }, [
    attributesByDomain,
    diagram,
    handleRemoveClass,
    handleRemoveRelationship,
    handleRemoveResource,
    hierarchyByIri,
    selection,
  ]);
};
